import { Link } from 'react-router-dom';
import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import {
  IconButton,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { ChangeEvent, useState } from "react";
import { deleteProject } from "../../api/admin";
import CustomTable from "../Reuseables/Tables/CustomTable";
import { useApplicationContext } from "../ContextProviders/ApplicationContext";
import { TableColumn } from "../../types/tableColumn";
import { CustomAccountInfo } from "../../types/customAccountInfo";
import { Project } from "../../types/project";
import { Role } from "../../types/enums";
import {
  findLabelByValue,
  getTranslatedDeviceTypes,
} from "../../helpers/utils";
import { stageOptions } from "../../helpers/selectOptions";
import { useOrganizationContext } from '../ContextProviders/OrganizationContext';

export default function MyProjects() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [areYouSure, setAreYouSure] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const { instance } = useMsal();
  const { showSnackbar } = useApplicationContext();
  const {activeWorkspace, removeProject} = useOrganizationContext()
  const [isMatching, setIsMatching] = useState(false);
  const account =
    instance && (instance.getActiveAccount() as CustomAccountInfo);
  const role = account.idTokenClaims.role;

  const handleOpenDialog = () => setAreYouSure(true);
  const handleCloseDialog = () => setAreYouSure(false);
  const _deleteProject = async () => {
    try {
      let response = await deleteProject(projectToDelete!);
      showSnackbar(projectToDelete?.projectCode + " was deleted.", "success");
      removeProject(response);
      handleCloseDialog();
    } catch (err: unknown) {
      if (err instanceof Error) {
        showSnackbar(err.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
    }
  };
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsMatching(e.target?.value === projectToDelete?.projectCode);
  };

  const columns: TableColumn[] = [
    { label: t("labels.projectCode"), key: "projectCode", sort: true },
    { label: t("labels.projectName"), key: "projectName", sort: true },
    { label: t("labels.location"), key: "location", sort: true },
    {
      label: t("labels.deviceType"),
      key: "deviceType",
      Cell: ({ value }) => {
        return getTranslatedDeviceTypes(value, t);
      },
      sort: true,
    },
    {
      label: t("labels.status"),
      key: "stage",
      Cell: ({ value }) => {
        return t(findLabelByValue(value, stageOptions));
      },
      sort: true,
    },
    {
      label: t("labels.overview"),
      key: "projectCode",
      sort: false,
      Cell: ({ value }) => {
        return (
          <Link to={`/myProjects/${value}`}>
          <IconButton
            aria-label="Navigate to project"
          >
            <AssessmentOutlined
              sx={{ ":hover": { color: theme.palette.primary.dark } }}
            />
          </IconButton>
          </Link>
        );
      },
    },
  ];
  if (role === Role.PrecureAdmin) {
    columns.push({
      key: "projectCode",
      label: t("labels.delete"),
      sort: false,
      Cell: ({ value }) => {
        return (
          <IconButton
            onClick={() => {
              setIsMatching(false);
              setProjectToDelete(
                activeWorkspace?.projects?.find((p) => p.projectCode === value) ??
                  null
              );
              handleOpenDialog();
            }}
          >
            <DeleteForeverIcon sx={{ ":hover": { color: "red" } }} />
          </IconButton>
        );
      },
    });
  }

  return (
    <Grid container mt={2}>
  
      <Grid item xs>
        <CustomTable
          title={t("labels.myProjects")}
          columns={columns}
          data={activeWorkspace?.projects ? activeWorkspace?.projects : []}
          defaultRowsPrPage={10}
        />
      </Grid>

      <Dialog open={areYouSure} onClose={handleCloseDialog}>
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <Box>
            You are about to delete project {projectToDelete?.projectCode}
          </Box>

          <Box mt={4}>
            <TextField
              sx={{ width: 200 }}
              inputProps={{ style: { fontSize: 32 } }}
              onChange={handleChange}
            />
          </Box>
          <Box mt={1}>Please confirm by typing the project code</Box>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            disabled={!isMatching}
            color="warning"
            onClick={_deleteProject}
          >
            Delete
          </Button>
          <Button variant="contained" onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
